import {
  FeEmptyArea,
  isEmptyAreaReviewed,
  isUnconfirmedSuggestion,
  useCoverageBetaStore,
  useIsEmptyAreaActive,
} from "@/store/useCoverageBetaStore";
import { qaItemStatusStyle } from "@/utils/build-coverage-beta-qa-result";
import { ActionIcon, Badge, CheckIcon } from "@mantine/core";
import { ProductImageThumbnail } from "./product-image-thumbnail/ProductImageThumbnail";
import { NoMatchThumbnail } from "./no-match-thumbnail/NoMatchThumbnail";
import { cn } from "@/utils/utils";
import {
  IconEyeOff,
  IconEye,
  IconTrash,
  IconArrowBackUp,
  IconRestore,
} from "@tabler/icons-react";
import { qaAreaChange } from "@/utils/coverage-qa-edit-diff";
import { produce } from "immer";
import { SampleOosPrediction } from "@/dev/sample-out-of-stock-predictions";
import {
  focusEmptyAreaBox,
  focusRefImgForArea,
} from "@/utils/coverage-beta-focus";

interface Props {
  item: FeEmptyArea;
  type: "oosConfirmed" | "emptyArea" | "deleted";
  out_of_stock_predictions: SampleOosPrediction[];
}

const ICON_SIZE = 14;

/** The row's check: violet for an unchanged SAM 2 suggestion (accepted on
 * submit), green once decided, faded gray while undecided. */
const reviewCheck = (
  isSuggestion: boolean,
  isDone: boolean,
  doneTitle: string,
  pendingTitle: string,
) => {
  if (isSuggestion)
    return {
      opacity: "opacity-100",
      title: "SAM 2 suggestion — accepted on submit",
      color: "text-violet-500",
    };
  if (isDone)
    return {
      opacity: "opacity-100",
      title: doneTitle,
      color: "text-green-500",
    };
  return { opacity: "opacity-40", title: pendingTitle, color: "text-gray-500" };
};

export const SummaryRow = (props: Props) => {
  const { item, type, out_of_stock_predictions } = props;
  const {
    feEmptyAreaIdx,
    source,
    selectedProducts,
    isHidden,
    isDeleted,
    isValid,
  } = item;

  // Reviewed = valid AND every reference product confirmed (see store helper).
  const isReviewed = isEmptyAreaReviewed(item);
  // An unchanged SAM 2 suggestion: submittable as is, shown violet like its box.
  const isSuggestion = isUnconfirmedSuggestion(item);

  // Reviewed with no product picked — the "No match" decision. Same check as
  // CoverageBetaRightFooter and QueryEmptyAreaDetectionBox.
  const isNoMatch = isValid && selectedProducts.length === 0;

  const emptyAreaCheck = reviewCheck(
    isSuggestion,
    isValid,
    "Reviewed",
    "Not yet reviewed",
  );
  const oosConfirmedCheck = reviewCheck(
    isSuggestion,
    isReviewed,
    "Confirmed",
    "Product not yet confirmed",
  );

  const isQaMode = useCoverageBetaStore((s) => s.isQaMode);
  const setActiveArea = useCoverageBetaStore((s) => s.setActiveArea);
  const isActiveEmptyAreaIdx = useIsEmptyAreaActive(feEmptyAreaIdx);

  const setIdxToFeEmptyArea = useCoverageBetaStore(
    (s) => s.setIdxToFeEmptyArea,
  );

  const deleteFeEmptyArea = useCoverageBetaStore((s) => s.deleteFeEmptyArea);
  const isReadOnly = useCoverageBetaStore((s) => s.isReadOnly);
  const qaReasons = useCoverageBetaStore(
    (s) => s.qaVerdicts[`area:${feEmptyAreaIdx}`],
  );
  // Editable Coverage QA: how the reviewer changed this area (null otherwise).
  const qaChange = useCoverageBetaStore((s) =>
    s.qaEditOriginal
      ? qaAreaChange(item, s.qaEditOriginal.idxToFeEmptyArea[feEmptyAreaIdx])
      : null,
  );
  // Grouped query images: which view the box is on (undefined when ungrouped).
  const viewNumber = useCoverageBetaStore(
    (s) =>
      s.groupImages.find(
        (image) => image.imageId === (item.imageId ?? s.primaryImageId),
      )?.viewNumber,
  );

  let sourceBadgeColor = "blue";
  if (type === "deleted") sourceBadgeColor = "gray";

  // In QA mode, a row's left border/checkmark reports this item's review
  // status at a glance — see getOrderedQaItems for what "deleted" covers.
  const qaStatus = qaItemStatusStyle(qaReasons);

  return (
    <div
      className={cn(
        "py-1 px-2 rounded flex gap-1.5 items-center hover:bg-gray-200/50 cursor-pointer",
        isActiveEmptyAreaIdx && "ring-2 ring-gray-200 bg-gray-200/25",
        isDeleted && "opacity-50",
        isDeleted && !isQaMode && "cursor-auto",
        isQaMode && qaStatus.borderClass,
      )}
      onClick={() => {
        const { confirming } = useCoverageBetaStore.getState();
        // Outside QA mode a deleted row isn't navigable (nothing to focus —
        // it's out of the task). In QA mode it needs to be, so a reviewer can
        // jump to and inspect what was deleted.
        if (isDeleted && !isQaMode) return;
        // Don't switch the active area while a confirm step is open for another
        // area — that would let the pick land on the wrong area.
        if (confirming != null && confirming.emptyAreaIdx !== feEmptyAreaIdx) {
          return;
        }
        setActiveArea(feEmptyAreaIdx);

        // Refocus the query view on this area's box — same as clicking the box
        // directly. Skipped if boxes are hidden or not mounted.
        focusEmptyAreaBox(feEmptyAreaIdx);
        focusRefImgForArea(feEmptyAreaIdx, out_of_stock_predictions);
      }}
    >
      {type === "emptyArea" && !isQaMode && (
        <span
          className={cn("flex items-center", emptyAreaCheck.opacity)}
          title={emptyAreaCheck.title}
        >
          <CheckIcon size={ICON_SIZE} className={emptyAreaCheck.color} />
        </span>
      )}

      {type === "oosConfirmed" && !isQaMode && (
        <span
          className={cn("flex items-center", oosConfirmedCheck.opacity)}
          title={oosConfirmedCheck.title}
        >
          <CheckIcon size={ICON_SIZE} className={oosConfirmedCheck.color} />
        </span>
      )}

      {isQaMode && (
        <span className="flex items-center" title={qaStatus.title}>
          <CheckIcon size={ICON_SIZE} className={qaStatus.iconClass} />
        </span>
      )}

      <div className="min-w-2 shrink-0 text-right pr-1">{feEmptyAreaIdx}</div>

      <Badge size="xs" color={sourceBadgeColor}>
        {source}
      </Badge>

      {viewNumber != null && (
        <Badge
          size="xs"
          variant="outline"
          color="gray"
          title="The grouped view this box is on"
        >
          View {viewNumber}
        </Badge>
      )}

      {/* Only for the Empty Area section: a deleted row keeps its old isValid
          for undo, and what it reports on submit is the deletion, not a
          no-match decision — so the tile would misread there. */}
      {type === "emptyArea" && isNoMatch && <NoMatchThumbnail />}

      {selectedProducts.slice(0, 3).map((p) => {
        return <ProductImageThumbnail key={p.id} product={p} />;
      })}
      {selectedProducts.length > 3 && (
        <span className="text-gray-500 font-semibold shrink-0">…</span>
      )}

      <div className="ml-auto flex items-center shrink-0">
        {/* Editable QA: the revert icon doubles as the "changed" marker. */}
        {qaChange && !isReadOnly && (
          <ActionIcon
            variant="transparent"
            color="grape"
            onClick={(e) => {
              e.stopPropagation();
              useCoverageBetaStore
                .getState()
                .revertAreaToLabeler(feEmptyAreaIdx);
            }}
            title={
              qaChange === "added"
                ? "Remove — the labeler didn't have this box"
                : "Revert to the labeler's answer"
            }
          >
            <IconRestore size={ICON_SIZE} />
          </ActionIcon>
        )}
        {isReadOnly ? null : isDeleted ? (
          <ActionIcon
            variant="transparent"
            color="blue"
            onClick={() => {
              const { idxToFeEmptyArea } = useCoverageBetaStore.getState();
              setIdxToFeEmptyArea(
                // Restore to the exact pre-delete state — just un-delete.
                produce(idxToFeEmptyArea, (draft) => {
                  draft[feEmptyAreaIdx].isDeleted = false;
                }),
              );
            }}
            title="Restore box"
          >
            <IconArrowBackUp size={ICON_SIZE} />
          </ActionIcon>
        ) : (
          <>
            <ActionIcon
              variant="transparent"
              color={isHidden ? "red" : isActiveEmptyAreaIdx ? "blue" : "gray"}
              disabled={selectedProducts.length > 0}
              onClick={() => {
                // Confirmed (product-bearing) areas must stay visible.
                if (selectedProducts.length > 0) return;
                const { idxToFeEmptyArea } = useCoverageBetaStore.getState();
                setIdxToFeEmptyArea(
                  produce(idxToFeEmptyArea, (draft) => {
                    draft[feEmptyAreaIdx].isHidden =
                      !draft[feEmptyAreaIdx].isHidden;
                  }),
                );
              }}
              title={
                selectedProducts.length > 0
                  ? "Areas with a product stay visible"
                  : isHidden
                    ? "Show box"
                    : "Hide box"
              }
            >
              {isHidden ? (
                <IconEyeOff size={ICON_SIZE} />
              ) : (
                <IconEye size={ICON_SIZE} />
              )}
            </ActionIcon>

            <ActionIcon
              className="hover:fill-red"
              variant="transparent"
              color="gray"
              onClick={(e) => {
                e.stopPropagation();
                deleteFeEmptyArea(feEmptyAreaIdx);
                useCoverageBetaStore
                  .getState()
                  .recordInteraction("delete_area", "click");
              }}
              title="Delete box"
            >
              <IconTrash
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "red";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "";
                }}
                className="hover:fill-red"
                size={ICON_SIZE}
              />
            </ActionIcon>
          </>
        )}
      </div>
    </div>
  );
};
